import { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Accordion } from '@tunein/web-ui-components';
import mint from '@tunein/mint';
import flow from 'lodash/flow';
import get from 'lodash/get';
import classNames from 'clsx';

// decorators
import cssVariables from 'src/common/styles/variables';
import withCollapsibleState from '../../decorators/withCollapsibleState';
import connectWithFailureState from '../../decorators/connectWithFailureState';

// components
import SeoHelmet from '../SeoHelmet';
import Text from '../Text';
import ContainerItemsLoader from '../containerItems/ContainerItemsLoader';
import DisplayAd from '../ads/DisplayAd';
import UpsellRightSidebar from '../subscription/UpsellRightSidebar';
import CollapsibleLabel from '../shared/CollapsibleLabel';

// utils
import { createProfilePageTitle } from '../utils/pageTitles';
import { createProfileMetaDescription } from '../utils/pageMetaDescriptions';
import { createProfilePageMetaKeywords } from '../utils/pageMetaKeywords';
import { createAndroidDeepLinks } from '../utils/createAndroidDeepLinks';
import { createInternationalSeoLinks } from '../utils/createInternationalSeoLinks';
import canonicalLinkObj from '../utils/canonicalLinkObj';
import isCBSAffiliated from '../../utils/guideItem/isCBSAffiliated';
import ifThenAppendCBSTritonScript from '../../utils/dom/ifThenAppendCBSTritonScript';
import playerStatuses from '../../utils/playerStatuses';
import { isMedium } from '../../utils/breakpoints';
import { buildGuideItemPathnameWithLogger } from '../../utils/guideItem/getGuideItemPathname';
import fullUrl from '../../utils/fullUrl';
import {
  behaviors as GuideItemBehaviors,
  isExpiredGame,
} from '../../utils/guideItemTypes';

// actions
import { tune } from '../../actions/player';
import { logClientError, logWebActivity } from '../../actions/logging';
import { fetchProfile } from '../../utils/connectors/fetchProfile';

// constants
import {
  LESS_INFORMATION,
  MORE_INFORMATION,
} from '../../constants/localizations/profile';

// css
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import getRadioContext from '../../routes/utils/getRadioContext';
import { selectIsAuthenticated } from '../../selectors/auth';
import css from './profile.module.scss';
import showProfileAdInContentArea from './utils/showProfileAdInContentArea';
import handleAutoplayCheck from './utils/handleAutoplayCheck';
import createOgMeta from './utils/createOgMeta';
import createOEmbedLinks from './utils/createOEmbedLinks';
import MoreInformation from './MoreInformation';
import Header from './Header';
import PlayButton from './PlayButton';

const styles = {
  information: {
    fontSize: cssVariables['--more-info-font-size'],
    fontWeight: 'bold',
  },
};

/* eslint-disable react/prefer-stateless-function */
export class Profile extends Component {
  static propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    guideItem: PropTypes.object.isRequired,
    routeProps: PropTypes.object.isRequired,
    selectedGuideId: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    shouldShowMore: PropTypes.bool.isRequired,
    handleMoreClick: PropTypes.func.isRequired,
    topicGuideId: PropTypes.string,
    audioClipId: PropTypes.string,
    playerStatus: PropTypes.string,
    isTunePrimed: PropTypes.bool,
    isMobile: PropTypes.bool.isRequired,
    allowAutoplay: PropTypes.bool.isRequired,
    breakpoint: PropTypes.number.isRequired,
    isTopicIdInvalid: PropTypes.bool.isRequired,
    resetCollapsibleState: PropTypes.func.isRequired,
    canShowAds: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);

    this.state = {
      canBrowserAutoplay: false,
    };

    const properties = get(props, 'guideItem.metadata.properties', {});
    mint.updateState(
      'countryRegionId',
      get(properties, 'location.countryRegionId'),
    );
    mint.updateState('stationLanguage', get(properties, 'station.language'));
    mint.updateState('selectedGuideId', props.selectedGuideId);
  }

  componentDidMount() {
    const {
      isMobile,
      playerStatus,
      isTunePrimed,
      guideItem,
      topicGuideId,
      isTopicIdInvalid,
    } = this.props;

    if (isCBSAffiliated(guideItem)) ifThenAppendCBSTritonScript();

    const behaviorAction = get(guideItem, 'behaviors.primaryButton.actionName');
    const subscriptionRequired =
      behaviorAction === GuideItemBehaviors.subscribe;
    const isPrimed = isMobile ? isTunePrimed : true;
    const isPlayerIdle = playerStatus === playerStatuses.idle;
    const canAutoPlay = !subscriptionRequired && isPrimed && isPlayerIdle;

    // In the case that a topicid is supplied, but it isn't valid (e.g. fails to load), we
    // shouldn't attempt to autoplay it.
    if ((!topicGuideId && canAutoPlay) || (!isTopicIdInvalid && canAutoPlay)) {
      handleAutoplayCheck(this.handleAutoplayReady.bind(this)).catch(() =>
        this.props.actions.logWebActivity(
          'autoPlayDisabled',
          true,
          guideItem.guideId,
        ),
      );
    }
  }

  componentDidUpdate(prevProps) {
    const {
      selectedGuideId,
      allowAutoplay,
      history,
      location,
      match,
      actions,
      resetCollapsibleState,
      isAuthenticated,
    } = this.props;

    if (prevProps.selectedGuideId !== selectedGuideId) {
      mint.updateState('selectedGuideId', selectedGuideId);
      resetCollapsibleState();
    }

    if (prevProps.isAuthenticated !== isAuthenticated) {
      actions.fetchProfile(getRadioContext, { history, location, match });
    }

    if (
      !prevProps.allowAutoplay &&
      allowAutoplay &&
      this.state.canBrowserAutoplay
    ) {
      this.autoPlay();
    }
  }

  componentWillUnmount() {
    // Notify Mint that we're no longer on the profile page
    mint.clearState('selectedGuideId');
  }

  handleAutoplayReady() {
    this.setState({ canBrowserAutoplay: true });
    this.autoPlay();
  }

  autoPlay() {
    const { formats, st } = this.context.location.query;
    const { guideItem, actions, topicGuideId, audioClipId, allowAutoplay } =
      this.props;

    if (allowAutoplay) {
      actions.tune({
        audioClipId,
        topicGuideId,
        guideItem,
        formats: formats ? formats.toLowerCase() : formats,
        isAutoplayed: true,
        playFromPosition: st,
      });
    }
  }

  render() {
    const {
      guideItem,
      selectedGuideId,
      shouldShowMore,
      handleMoreClick,
      breakpoint,
      routeProps,
      canShowAds,
      actions,
    } = this.props;
    const { getLocalizedText, location } = this.context;
    const canExpand = get(guideItem, 'actions.expand.canExpand');
    const properties = get(guideItem, 'metadata.properties', {});
    const pageTitle = createProfilePageTitle({
      getLocalizedText,
      title: guideItem.title,
      properties,
      type: guideItem.type,
    });
    const description = createProfileMetaDescription({
      getLocalizedText,
      type: guideItem.type,
      description: guideItem.description,
      title: guideItem.title,
      ...properties,
    });
    const keywords = createProfilePageMetaKeywords({
      title: guideItem.title,
      rootGenre: guideItem.rootGenre,
      keywords: get(properties, 'seoInfo.keywords'),
    });
    const guideItemPathName = buildGuideItemPathnameWithLogger(guideItem)(
      actions.logClientError,
    );
    const links = [
      canonicalLinkObj(guideItemPathName),
      ...createInternationalSeoLinks(guideItemPathName),
      ...createOEmbedLinks(
        guideItem.guideId,
        guideItem.title,
        get(guideItem, 'actions.embed'),
      ),
      ...createAndroidDeepLinks(guideItem.guideId),
    ];

    const fullUrlWithAbsoluteRoot = fullUrl(guideItemPathName);

    const meta = [
      ...(isExpiredGame(properties.program)
        ? [{ name: 'robots', content: 'noindex' }]
        : []),
      ...createOgMeta(guideItem, fullUrlWithAbsoluteRoot),
    ];

    /* Big play button should be hidden for profiles that don't have a stream(canPlay=false)
    or no guideId for the tune call or podcast profile and experiment is off.
     */
    const hidePlayButton =
      !get(guideItem, 'actions.play.canPlay') ||
      !get(guideItem, 'actions.play.guideId');

    const shouldShowProfileAdInContentArea =
      showProfileAdInContentArea(breakpoint) && canShowAds;

    return (
      <div className={css.container}>
        <SeoHelmet
          title={pageTitle}
          description={description}
          keywords={keywords}
          meta={meta}
          links={links}
        />
        <div style={{ opacity: guideItem.isFetching ? 0.5 : 1 }}>
          <Header
            selectedGuideId={selectedGuideId}
            location={location}
            routeProps={routeProps}
            breakpoint={breakpoint}
            canShowAds={canShowAds}
          />
          <section className={classNames('row', css.playSection)}>
            <div className="col-sm-12">
              {canExpand && (
                <div className={css.collapsibleLabelContainer}>
                  <CollapsibleLabel
                    shouldShowMore={shouldShowMore}
                    onMoreInfoClick={handleMoreClick}
                    moreText={<Text name={MORE_INFORMATION} />}
                    lessText={<Text name={LESS_INFORMATION} />}
                    labelStyle={styles.information}
                  />
                </div>
              )}
              <div
                className={classNames(css.dividerContainer, {
                  [css.noCollapsibleLabelContainer]: !canExpand,
                })}
              >
                <hr />
              </div>
              {!hidePlayButton && (
                <div className={css.playButtonContainer}>
                  <PlayButton guideItem={guideItem} />
                </div>
              )}
            </div>
          </section>

          {canShowAds && !isMedium(breakpoint) && (
            <section className="row">
              <div className="col-sm-12">
                <DisplayAd
                  id="profile_mid"
                  slotName="profile_mid"
                  style={{ padding: '15px 0' }}
                  guideId={get(routeProps, 'guideContext.guideId')}
                  matchUrl={routeProps.matchUrl}
                  isProfile
                />
              </div>
            </section>
          )}

          <section className="row">
            <div
              className={
                shouldShowProfileAdInContentArea ? 'col-xs-7' : 'col-xs-12'
              }
            >
              {canExpand && (
                <Accordion isExpanded={shouldShowMore}>
                  <div data-testid="moreInfoContent">
                    <MoreInformation guideItem={guideItem} />
                    <div className={css.dividerContainer}>
                      <hr />
                    </div>
                  </div>
                </Accordion>
              )}
              <ContainerItemsLoader
                guideItem={guideItem}
                items={guideItem.containerItems}
                itemsStyles={get(guideItem, 'metadata.styles', {})}
                isFetching={guideItem.isFetching}
                selectedContentId={selectedGuideId}
                breakpoint={breakpoint}
                isProfilePage
              />
            </div>
            {shouldShowProfileAdInContentArea && (
              <div className="col-xs-4 col-xs-offset-1">
                <DisplayAd
                  id="profile_side"
                  slotName="profile_side"
                  style={{
                    float: 'right',
                    paddingLeft: cssVariables['--extra-margin'],
                    paddingBottom: '25px',
                  }}
                  guideId={get(routeProps, 'guideContext.guideId')}
                  matchUrl={routeProps.matchUrl}
                  isProfile
                />
                <UpsellRightSidebar guideId={guideItem.id} isProfile />
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }
}

function getProfile(state, props) {
  return state.profiles[props?.routeProps?.guideContext?.guideId];
}

export function mapStateToProps(state, ownProps) {
  const { guideId, topicGuideId, audioClipId } =
    ownProps?.routeProps?.guideContext || {};
  const { profiles, player, app } = state;
  const { playerStatus, isTunePrimed } = player;
  const { isMobile, allowAutoplay } = app;
  const guideItem = profiles[guideId];
  const hasTopicFetchFailed = get(profiles, [topicGuideId, 'hasFailed']);

  return {
    playerStatus,
    isTunePrimed,
    isAuthenticated: selectIsAuthenticated(state),
    selectedGuideId: guideId,
    topicGuideId: topicGuideId || null,
    audioClipId: audioClipId || null,
    guideItem,
    isMobile,
    allowAutoplay,
    isTopicIdInvalid: !!hasTopicFetchFailed,
  };
}

const actions = {
  fetchProfile,
  tune,
  logClientError,
  logWebActivity,
};

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch),
  };
}

export default flow(
  withCollapsibleState,
  connectWithFailureState(getProfile),
  connect(mapStateToProps, mapDispatchToProps),
)(Profile);
